import { type DocumentPeriodsFilter } from 'api/documentsApi';
import TreeMultiSelector, { type TreeNode } from 'components/TreeMultiSelector/TreeMultiSelector';
import { debounce,
  getPeriodOptions } from 'helpers/helpers';
import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { type AppDispatch } from 'store';
import {
  type DocumentsStoreState,
  fetchCategories,
  type Filters,
  type FilterStoreState,
  type OrganizationsStoreState,
  type PeriodFilters,
  setUsualDocumentsFilters,
} from 'store/slices';
import { type NestedOrganization } from 'types';

const getSelectedOptions = (periodStrings: string[]): TreeNode[] => {
  const yearMap = new Map<number | string, Set<string>>();

  for (const period of periodStrings) {
    const [
      year,
      quarter,
    ] = period.split('/');

    const quarters = yearMap.get(year) || yearMap.set(year, new Set()).get(year);
    quarters?.add(quarter);
  }

  return Array.from(yearMap.entries()).map(([
    year,
    quarters,
  ]) => {
    return {
      children: Array.from(quarters).map((quarter) => {
        return {
          id: `${year}/${quarter}`,
          name: `${quarter}`,
        };
      }),
      id: `${year}`,
      name: `${year}`,
    };
  });
};

export const DocumentsPeriodSelector = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    displayedOrganizationWithPermissions,
  } = useSelector<{
    organizations: OrganizationsStoreState,
  }>((state) => state.organizations) as OrganizationsStoreState;

  const activeFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.activeFilters) as Filters | null;

  const periodFilters = useSelector<{
    filters: FilterStoreState,
  }>((state) => state.filters.usualDocumentsFilter) as PeriodFilters | null;

  const {
    categoriesPending,
    displayType,
  } = useSelector<{ documents: DocumentsStoreState, }>(
    (state) => state.documents,
  ) as DocumentsStoreState;

  const debouncedFetchCategories = useCallback(
    debounce((filters: DocumentPeriodsFilter) => {
      dispatch(fetchCategories(filters));
    }, 700),
    [
      dispatch,
    ],
  );

  const checkPeriod = (years: string[], quarters: string[]) => {
    if (categoriesPending) {
      return;
    }

    // change only selected quarters and years, allQuarters and allYears are not changed
    dispatch(setUsualDocumentsFilters({
      allQuarters: periodFilters?.allQuarters as string[],
      allYears: periodFilters?.allYears as string[],
      organizationId: activeFilters?.organizationId,
      selectedQuarters: quarters,
      selectedYears: years,
    } as PeriodFilters));

    if (activeFilters) {
      debouncedFetchCategories({
        ...activeFilters,
        displayType: displayType.id,
        firmId: activeFilters.organizationId,
        quarters,
      });
    }
  };

  const [periodOptions, setPeriodOptions] = useState<TreeNode[]>([]);

  /**
   * Updates period filters when new period options are available
   *
   * This function processes newly fetched period options and handles the auto-selection
   * of newly added periods. It:
   * 1. Validates that the current organization ID matches the one in period filters
   * 2. Extracts all years and quarters from the provided period options
   * 3. Identifies any quarters that weren't previously in allQuarters
   * 4. Automatically adds these new quarters to the selected quarters and years
   * 5. Updates the Redux store with the new selections if changes were detected
   * 6. Triggers a fetch of categories with the updated selections
   *
   * @param periodOptionsFromOrg - The period options retrieved from the organization data
   * @param currentOrganizationId - The ID of the currently active organization
   */
  const updatePeriodFilters = (periodOptionsFromOrg: TreeNode[], currentOrganizationId: string) => {
    if (!periodFilters || !activeFilters) {
      return;
    }

    // currentOrgId and orgId from periodFilters should match, otherwise we'll add newQuarters from another organization!
    if (currentOrganizationId !== periodFilters.organizationId) {
      return;
    }

    const allYears = periodOptionsFromOrg.map((opt) => opt.id);
    const allQuarters = periodOptionsFromOrg.flatMap((opt) => opt.children?.map((child) => child.id)) as string[];
    const selectedYears = new Set<string>(periodFilters.selectedYears);
    const selectedQuarters = new Set<string>(periodFilters.selectedQuarters);
    let hasNewQuarters = false;

    for (const quarter of allQuarters) {
      if (!periodFilters.allQuarters.includes(quarter)) {
        const year = quarter.split('/')[0];
        selectedYears.add(year);
        selectedQuarters.add(quarter);
        hasNewQuarters = true;
      }
    }

    if (hasNewQuarters) {
      dispatch(setUsualDocumentsFilters(
        {
          allQuarters,
          allYears,
          organizationId: activeFilters.organizationId,
          selectedQuarters: Array.from(selectedQuarters),
          selectedYears: Array.from(selectedYears),
        },
      ));

      debouncedFetchCategories(
        { ...activeFilters, displayType: displayType.id, firmId: activeFilters.organizationId, quarters: Array.from(selectedQuarters)},
      );
    }
  };

  useEffect(() => {
    if (!displayedOrganizationWithPermissions) {
      return;
    }

    const periodOptionsFromOrg = getPeriodOptions(displayedOrganizationWithPermissions as NestedOrganization);
    setPeriodOptions(periodOptionsFromOrg);
    updatePeriodFilters(periodOptionsFromOrg, displayedOrganizationWithPermissions.organizationId);
  }, [displayedOrganizationWithPermissions]);

  return <TreeMultiSelector
    dataSelectionValue='period-filter-multiselector'
    disabled={!getPeriodOptions(
      displayedOrganizationWithPermissions as NestedOrganization).length}
    inputStyle='bg-white-999 cursor-pointer relative flex flex-col justify-center w-auto items-end h-[2rem] w-[7rem] py-0 rounded-main border border-black-200'
    isResettable
    onCheck={checkPeriod}
    placeholder='All periods'
    selectedData={getSelectedOptions(periodFilters?.selectedQuarters as string[])}
    togglingOptionsDisabled={categoriesPending}
    treeData={periodOptions}
  />;
};
